import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdDeleteForever, MdInventory2, MdListAlt, MdPictureAsPdf } from 'react-icons/md';

const BASE_URL = 'https://inventariosbackend.velasresorts.com/inventarios';

const HOTEL_MAP: Record<string, number> = {
  GVRN: 1, GVRM: 2, GVBLC: 3, GVLC: 4, VV: 5, CV: 6, MDC: 7, CC: 8, DEMO: 9,
};

interface ActivoBaja {
  id: number;
  folio: string;
  nombre: string;
  serial: string;
  modelo: string;
}

interface GrupoBaja {
  descargaId: number;
  pdfDisponible: boolean;
  fecha_baja: string;
  responsable: string;
  responsable_avatar: string;
  motivos: string[];
  activos: ActivoBaja[];
  cantidadActivos: number;
}

const formatearFecha = (fecha: string) => {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
};

export default function HistorialBajas() {
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const textPrimary = useColorModeValue('gray.800', 'whiteAlpha.900');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');

  const [hotelSeleccionado, setHotelSeleccionado] = useState(
    () => sessionStorage.getItem('hotelSeleccionado') || 'VV'
  );
  const [grupos, setGrupos] = useState<GrupoBaja[]>([]);
  const [cargando, setCargando] = useState(true);
  const [descargandoId, setDescargandoId] = useState<number | null>(null);

  const mostrarError = (description: string) => {
    toast({ title: 'Error', description, status: 'error', duration: 4000, isClosable: true });
  };

  const cargarHistorial = async (hotelAbrev: string) => {
    setCargando(true);
    try {
      const hotelId = HOTEL_MAP[hotelAbrev] || 5;
      const res = await fetch(`${BASE_URL}/historial/${hotelId}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setGrupos(Array.isArray(data) ? data : []);
    } catch {
      mostrarError('No se pudo cargar el historial de bajas.');
      setGrupos([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarHistorial(hotelSeleccionado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelSeleccionado]);

  useEffect(() => {
    const handleHotelCambiado = () => {
      setHotelSeleccionado(sessionStorage.getItem('hotelSeleccionado') || 'VV');
    };
    window.addEventListener('hotelCambiado', handleHotelCambiado);
    return () => window.removeEventListener('hotelCambiado', handleHotelCambiado);
  }, []);

  const handleDescargarPdf = (grupo: GrupoBaja) => {
    if (!grupo.pdfDisponible) {
      mostrarError('El PDF de esta baja ya no está disponible en el servidor.');
      return;
    }
    setDescargandoId(grupo.descargaId);
    // Descarga directa: el backend ya manda Content-Disposition: attachment.
    window.open(`${BASE_URL}/bajas/descargar-pdf/${grupo.descargaId}`, '_blank');
    setTimeout(() => setDescargandoId(null), 1000);
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: 4, md: 6 }} pb={6}>
      <Flex justify="space-between" align={{ base: 'start', md: 'center' }} direction={{ base: 'column', md: 'row' }} mb={6} gap={3}>
        <Box>
          <Heading size="lg">Historial de Bajas</Heading>
          <Text fontSize="sm" color={textSecondary} mt={1}>
            Activos dados de baja definitivamente, con su PDF de respaldo.
          </Text>
        </Box>
        <Badge colorScheme="gray" px={3} py={2} borderRadius="md" fontSize="sm">
          Hotel: {hotelSeleccionado || 'Sin hotel'}
        </Badge>
      </Flex>

      <Card variant="outline" bg={cardBg} borderColor={borderColor} p="0px" overflow="hidden">
        <CardHeader bg={subtleBg} py={4} px={6}>
          <HStack spacing={2}>
            <Icon as={MdDeleteForever} color="red.400" w={5} h={5} />
            <Heading size="sm">Bajas registradas</Heading>
          </HStack>
        </CardHeader>

        <CardBody px={6} py={6}>
          {cargando ? (
            <Center py={14}>
              <VStack spacing={3}>
                <Spinner size="lg" color="red.400" thickness="3px" />
                <Text fontSize="sm" color={textSecondary}>Cargando historial...</Text>
              </VStack>
            </Center>
          ) : grupos.length === 0 ? (
            <Center py={14}>
              <VStack spacing={3}>
                <Icon as={MdListAlt} w="40px" h="40px" color="gray.300" />
                <Text fontSize="sm" color={textSecondary}>No hay bajas registradas para este hotel</Text>
              </VStack>
            </Center>
          ) : (
            <VStack spacing={3} align="stretch">
              {grupos.map((grupo) => (
                <Box
                  key={grupo.descargaId}
                  p={4}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="12px"
                  bg={cardBg}
                  borderLeft="3px solid"
                  borderLeftColor="red.400"
                >
                  <Flex justify="space-between" align="start" wrap="wrap" gap={3}>
                    <Box flex={1} minW="240px">
                      <HStack spacing={2} mb={2} flexWrap="wrap">
                        <Icon as={MdInventory2} color={textSecondary} />
                        <Text fontSize="sm" fontWeight="700" color={textPrimary}>
                          {formatearFecha(grupo.fecha_baja)}
                        </Text>
                        <Badge colorScheme="red" fontSize="10px" variant="subtle">
                          {grupo.cantidadActivos} activo{grupo.cantidadActivos !== 1 ? 's' : ''}
                        </Badge>
                        {!grupo.pdfDisponible && (
                          <Badge colorScheme="gray" fontSize="10px" variant="subtle">
                            PDF no disponible
                          </Badge>
                        )}
                      </HStack>

                      <Text fontSize="xs" color={textSecondary}>
                        Responsable: <Text as="span" fontWeight="600" color={textPrimary}>{grupo.responsable}</Text>
                      </Text>

                      {grupo.motivos.length > 0 && (
                        <Text fontSize="xs" color={textSecondary} mt={1} noOfLines={2}>
                          Motivo: {grupo.motivos.join(' · ')}
                        </Text>
                      )}

                      <Wrap mt={2} spacing={1}>
                        {grupo.activos.map((activo) => (
                          <WrapItem key={activo.id}>
                            <Badge colorScheme="cyan" fontSize="10px" variant="subtle" title={activo.nombre}>
                              {activo.folio}
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>

                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      leftIcon={<Icon as={MdPictureAsPdf} />}
                      isLoading={descargandoId === grupo.descargaId}
                      isDisabled={!grupo.pdfDisponible}
                      onClick={() => handleDescargarPdf(grupo)}
                    >
                      PDF
                    </Button>
                  </Flex>
                </Box>
              ))}
            </VStack>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}
